/**
 * 协调费用相关API服务
 */
import api from './api';
import {
  CoordinationCost,
  CoordinationCostType,
  CoordinationCostSubType,
  CoordinationApprovalStatus,
  CoordinationSourceType,
  CoordinationGovernanceStats,
  CreateCoordinationCostData,
  UpdateCoordinationCostData,
  ApiResponse,
  PaginatedResponse,
} from '../types';

// 协调费用统计数据
export interface CoordinationCostStats {
  totalCost: number;
  byType: {
    management: number;
    coordination: number;
    misc: number;
    emergency: number;
  };
  recordCount: number;
  riskRecordCount: number;
}

// 协调费用查询参数
export interface CoordinationCostQueryParams {
  projectId?: string;
  costType?: CoordinationCostType;
  subType?: CoordinationCostSubType;
  approvalStatus?: CoordinationApprovalStatus;
  sourceType?: CoordinationSourceType;
  riskFlag?: boolean;
  keyword?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export class CoordinationCostService {
  /**
   * 获取协调费用列表
   */
  static async getCoordinationCosts(
    params: CoordinationCostQueryParams = {}
  ): Promise<PaginatedResponse<CoordinationCost>> {
    return api.get('/coordination-costs', { params }) as Promise<PaginatedResponse<CoordinationCost>>;
  }

  /**
   * 根据ID获取协调费用详情
   */
  static async getCoordinationCostById(id: string): Promise<ApiResponse<CoordinationCost>> {
    return api.get(`/coordination-costs/${id}`) as Promise<ApiResponse<CoordinationCost>>;
  }

  /**
   * 创建协调费用记录
   */
  static async createCoordinationCost(data: CreateCoordinationCostData): Promise<ApiResponse<CoordinationCost>> {
    return api.post('/coordination-costs', data) as Promise<ApiResponse<CoordinationCost>>;
  }

  /**
   * 更新协调费用记录
   */
  static async updateCoordinationCost(
    id: string,
    data: UpdateCoordinationCostData
  ): Promise<ApiResponse<CoordinationCost>> {
    return api.put(`/coordination-costs/${id}`, data) as Promise<ApiResponse<CoordinationCost>>;
  }

  /**
   * 删除协调费用记录
   */
  static async deleteCoordinationCost(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/coordination-costs/${id}`) as Promise<ApiResponse<void>>;
  }

  /**
   * 获取项目协调费用统计
   */
  static async getProjectCoordinationStats(
    projectId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<CoordinationCostStats>> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return api.get(`/coordination-costs/stats/${projectId}`, { params }) as Promise<ApiResponse<CoordinationCostStats>>;
  }

  static async getProjectGovernanceStats(
    projectId: string,
    options: {
      startDate?: string;
      endDate?: string;
      includeRiskOnly?: boolean;
    } = {}
  ): Promise<ApiResponse<CoordinationGovernanceStats>> {
    const params: Record<string, string | boolean> = {};
    if (options.startDate) params.startDate = options.startDate;
    if (options.endDate) params.endDate = options.endDate;
    if (options.includeRiskOnly !== undefined) params.includeRiskOnly = options.includeRiskOnly;

    return api.get(`/coordination-costs/governance-stats/${projectId}`, { params }) as Promise<ApiResponse<CoordinationGovernanceStats>>;
  }

  /**
   * 获取协调费用类型标签
   */
  static getCostTypeLabel(type: CoordinationCostType): string {
    const labels: Record<CoordinationCostType, string> = {
      [CoordinationCostType.MANAGEMENT]: '管理费',
      [CoordinationCostType.COORDINATION]: '协调费',
      [CoordinationCostType.MISC]: '杂费',
      [CoordinationCostType.EMERGENCY]: '应急费用',
    };
    return labels[type] || type;
  }

  /**
   * 获取所有协调费用类型选项
   */
  static getCostTypeOptions(): Array<{ value: CoordinationCostType; label: string }> {
    return [
      { value: CoordinationCostType.MANAGEMENT, label: '管理费' },
      { value: CoordinationCostType.COORDINATION, label: '协调费' },
      { value: CoordinationCostType.MISC, label: '杂费' },
      { value: CoordinationCostType.EMERGENCY, label: '应急费用' },
    ];
  }

  static getSubTypeOptions(): Array<{ value: CoordinationCostSubType; label: string }> {
    return [
      { value: CoordinationCostSubType.GENERAL, label: '通用支出' },
      { value: CoordinationCostSubType.SITE_SUPPORT, label: '现场保障' },
      { value: CoordinationCostSubType.EXTERNAL_COMMUNICATION, label: '外部沟通' },
      { value: CoordinationCostSubType.TEMPORARY_EXPENSE, label: '临时费用' },
      { value: CoordinationCostSubType.HOSPITALITY, label: '招待支出' },
      { value: CoordinationCostSubType.GOVERNMENT_COORDINATION, label: '政府协调' },
      { value: CoordinationCostSubType.TRAFFIC, label: '交通支出' },
      { value: CoordinationCostSubType.OTHER, label: '其他' },
    ];
  }

  static getApprovalStatusOptions(): Array<{ value: CoordinationApprovalStatus; label: string }> {
    return [
      { value: CoordinationApprovalStatus.NONE, label: '未发起' },
      { value: CoordinationApprovalStatus.PENDING, label: '审批中' },
      { value: CoordinationApprovalStatus.APPROVED, label: '已通过' },
      { value: CoordinationApprovalStatus.REJECTED, label: '已驳回' },
    ];
  }

  static getSourceTypeOptions(): Array<{ value: CoordinationSourceType; label: string }> {
    return [
      { value: CoordinationSourceType.MANUAL, label: '手工录入' },
      { value: CoordinationSourceType.IMPORT, label: '导入' },
      { value: CoordinationSourceType.SYSTEM, label: '系统生成' },
    ];
  }

  static getSubTypeLabel(type: CoordinationCostSubType): string {
    return this.getSubTypeOptions().find(item => item.value === type)?.label || type;
  }

  static getApprovalStatusLabel(status: CoordinationApprovalStatus): string {
    return this.getApprovalStatusOptions().find(item => item.value === status)?.label || status;
  }

  static getSourceTypeLabel(type: CoordinationSourceType): string {
    return this.getSourceTypeOptions().find(item => item.value === type)?.label || type;
  }
}
